import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { User, UserTab } from '../../../../../auth/models';
import { ToastService } from '../../../../../main/services';
import { Tender } from '../../../../models';
import { SuppliersService, TenderSuppliersService } from "../../../../services";


@Component({
    selector: 'jft-add-invited-suppliers-form-modal',
    templateUrl: './add-invited-suppliers-form-modal.component.html',
    styleUrls: ['./add-invited-suppliers-form-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddInvitedSuppliersFormModalComponent implements OnInit {
    public allSuppliersButton: boolean = true;
    public supplierUsers: User[] = [];
    public filteredSupplierUsers: User[] = [];
    public selectedSupplierUsers: User[] = [];
    public invitedUsers: User[] = [];
    public loading = false;
    public showAllUsers: boolean = false;
    public availableTags: UserTab[] = [];
    public selectedTags: any[] = [];
    public tags: any[] = [];
    public searchTerm: string = '';

    constructor(
        private toast: ToastService,
        public tender: Tender,
        private activeModal: NgbActiveModal,
        public service: SuppliersService,
        public tenderService: TenderSuppliersService
    ) {
    }

    public ngOnInit() {
        this.filteredSupplierUsers = [];
        this.getSupplierUsers();
        this.initAvailableTabs();
    }

    private initAvailableTabs() {
        this.availableTags.forEach((c, i) => {
            this.tags.push({id: c.id, name: c.name});
        });
    }

    public getSupplierUsers(filterTagIds = []){
        this.service.fetchSupplierUsers(filterTagIds).subscribe((data) => {
          this.supplierUsers = data.sort((a, b) => a.company.name.localeCompare(b.company.name));
          if (this.showAllUsers != true){
              this.getInvitedSupplierUsers();
          }
          this.applySearch();
        }, (e) => {
            this.toast.error(e);
        });
    }

    public tagChange(tags){
        this.getSupplierUsers(tags.map(item=>item.id));
    }

    public onSearchChange() {
        this.applySearch();
    }

    private applySearch() {
        if (!this.searchTerm || this.searchTerm.trim() === '') {
            this.filteredSupplierUsers = [...this.supplierUsers];
            return;
        }

        const term = this.searchTerm.toLowerCase().trim();
        this.filteredSupplierUsers = this.supplierUsers.filter(supplier => {
            const companyName = supplier.company?.name?.toLowerCase() || '';
            const email = supplier.email?.toLowerCase() || '';
            const alias = supplier.supplierAlias?.toLowerCase() || '';

            return companyName.includes(term) ||
                   email.includes(term) ||
                   alias.includes(term);
        });
    }

    public getInvitedSupplierUsers(){
        this.tenderService.fetchInvitedSupplierUsers(this.tender.id).subscribe((data) => {
            this.invitedUsers = data;
            let invitedUsersIds = this.invitedUsers.map(us => us.id);
            this.supplierUsers = this.supplierUsers.filter(value => !invitedUsersIds.includes(value.id))
            this.applySearch();
        }, (e) => {});
    }

    public addSelected() {
        if (!this.selectedSupplierUsers.length) {
            this.toast.error('No suppliers selected');
            return;
        }
        this.addSuppliersToTender()
    }

    public addAllSuppliers() {
        this.selectedSupplierUsers = JSON.parse(JSON.stringify(this.supplierUsers));
        this.addSelected();
    }

    private addSuppliersToTender() {
        this.loading = false;
        this.activeModal.close({
            users: this.selectedSupplierUsers
        });
    }

    public onChangeSupplier(supplier, event) {
        let user = supplier
        if (event.target.checked) {
            this.selectedSupplierUsers.push(user);
        } else {
            this.selectedSupplierUsers = this.selectedSupplierUsers.filter(value => value.id != user.id)
        }
    }

    public isSupplierSelected(supplier: User): boolean {
        return this.selectedSupplierUsers.some(s => s.id === supplier.id);
    }

    // Counts reflect the active tag filter and already exclude suppliers
    // already invited to this tender (removed from supplierUsers above).
    public get availableContactsCount(): number {
        return this.supplierUsers.length;
    }

    public get availableCompaniesCount(): number {
        return new Set(this.supplierUsers.map(u => u.company?.id)).size;
    }

    public get selectedContactsCount(): number {
        return this.selectedSupplierUsers.length;
    }

    public get selectedCompaniesCount(): number {
        return new Set(this.selectedSupplierUsers.map(u => u.company?.id)).size;
    }

    public get allVisibleSelected(): boolean {
        if (!this.supplierUsers.length) {
            return false;
        }
        // Reflects the FULL tag-filtered list, not the in-memory search subset,
        // so an active search term cannot make "select all" silently under-select.
        return this.supplierUsers.every(supplier => this.isSupplierSelected(supplier));
    }

    public toggleSelectAll(event: any) {
        if (event.target.checked) {
            // Select every supplier in the (tag-filtered) list, regardless of the search term.
            this.supplierUsers.forEach(supplier => {
                if (!this.isSupplierSelected(supplier)) {
                    this.selectedSupplierUsers.push(supplier);
                }
            });
        } else {
            // Deselect the entire list.
            const allIds = this.supplierUsers.map(s => s.id);
            this.selectedSupplierUsers = this.selectedSupplierUsers.filter(
                s => !allIds.includes(s.id)
            );
        }
    }

    public addContact(supplier: User) {
        this.activeModal.close({ users: [supplier] });
    }

    public close() {
        this.activeModal.dismiss();
    }
}
